import { UcoastEl } from '@/scripts/core/UcoastEl'
import { qsRequired, qsOptional } from '@/scripts/core/global'

export class StickyAddToBag extends UcoastEl {
	static htmlSelector = 'sticky-add-to-bag'

	private sourceButton: HTMLButtonElement
	private stickyButton: HTMLButtonElement
	private actionTextEl: HTMLElement
	private sourceActionTextEl: HTMLElement
	private sourceObserver: IntersectionObserver | null = null
	private footerObserver: IntersectionObserver | null = null
	private mutationObserver: MutationObserver | null = null
	private sourcePastViewport = false
	private footerInViewport = false

	constructor() {
		super()
		this.sourceButton = qsRequired<HTMLButtonElement>(
			'button[type="submit"]',
			qsRequired('product-detail')
		)
		this.stickyButton = qsRequired<HTMLButtonElement>(
			'.sticky-add-to-bag__button',
			this
		)
		this.actionTextEl = qsRequired('[data-sticky-action-text]', this)
		this.sourceActionTextEl = qsRequired(
			'[data-dynamic="action"]',
			qsRequired('product-detail')
		)
	}

	override connectedCallback() {
		super.connectedCallback()
		this.syncState()
		this.setupSourceObserver()
		this.setupFooterObserver()
		this.setupMutationObserver()
		this.stickyButton.addEventListener('click', this.handleClick)
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.sourceObserver?.disconnect()
		this.footerObserver?.disconnect()
		this.mutationObserver?.disconnect()
		this.stickyButton.removeEventListener('click', this.handleClick)
	}

	private handleClick = () => {
		this.sourceButton.click()
	}

	private setupSourceObserver() {
		this.sourceObserver = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				this.sourcePastViewport = !entry.isIntersecting && entry.boundingClientRect.bottom < 0
			}
			this.updateVisibility()
		})
		this.sourceObserver.observe(this.sourceButton)
	}

	private setupFooterObserver() {
		const footer = qsOptional('footer.footer')
		if (!footer) return
		this.footerObserver = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				this.footerInViewport = entry.isIntersecting
			}
			this.updateVisibility()
		})
		this.footerObserver.observe(footer)
	}

	private setupMutationObserver() {
		this.mutationObserver = new MutationObserver(() => {
			this.syncState()
		})
		this.mutationObserver.observe(this.sourceButton, {
			attributes: true,
			attributeFilter: [
				'data-oos',
				'aria-disabled',
				'disabled',
				'data-uc-loading',
			],
		})
		this.mutationObserver.observe(this.sourceActionTextEl, {
			childList: true,
			characterData: true,
			subtree: true,
		})
	}

	private syncState() {
		const syncAttr = (attr: string) => {
			if (this.sourceButton.hasAttribute(attr)) {
				this.stickyButton.setAttribute(
					attr,
					this.sourceButton.getAttribute(attr) ?? ''
				)
			} else {
				this.stickyButton.removeAttribute(attr)
			}
		}
		syncAttr('data-oos')
		syncAttr('aria-disabled')
		syncAttr('data-uc-loading')
		if (this.sourceButton.disabled) {
			this.stickyButton.disabled = true
		} else {
			this.stickyButton.disabled = false
		}
		this.actionTextEl.innerHTML = this.sourceActionTextEl.innerHTML
	}

	private updateVisibility() {
		requestAnimationFrame(() => {
			if (this.sourcePastViewport && !this.footerInViewport) {
				this.setAttribute('data-visible', '')
			} else {
				this.removeAttribute('data-visible')
			}
		})
	}
}
